import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner, Alert, Badge, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function EmailSequences() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Email editor modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Customer experience context (not currently fetched - no GET endpoint)
  const [customerExperience] = useState(null);
  // Upsell options for email sequences
  const [upsellOptions, setUpsellOptions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (preserveSelection = false) => {
    const currentSelection = selectedProduct;
    try {
      // Fetch products
      const productsRes = await fetch('/api/funnels/products', { credentials: 'include' });
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || data || []);
        // Only auto-select first product on initial load, not on refresh
        if (!preserveSelection && data.products?.length > 0 && !currentSelection) {
          setSelectedProduct(data.products[0].id);
        }
      }

      // Fetch email sequences
      const seqRes = await fetch('/api/email-sequences', { credentials: 'include' });
      if (seqRes.ok) {
        const data = await seqRes.json();
        setSequences(data.sequences || []);
      }

      // Fetch upgrade paths for upsell options
      const pathsRes = await fetch('/api/upgrade-paths/get', { credentials: 'include' });
      if (pathsRes.ok) {
        const data = await pathsRes.json();
        setUpsellOptions(data.upgrade_paths || []);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getSequenceForProduct = (productId) => {
    return sequences.filter(s => s.product_id === productId).sort((a, b) => a.send_day - b.send_day);
  };

  const handleEditEmail = (email) => {
    setEditingEmail({ ...email });
    setShowEmailModal(true);
  };

  const handleAddEmail = () => {
    const productSequence = getSequenceForProduct(selectedProduct);
    const nextDay = productSequence.length > 0
      ? Math.max(...productSequence.map(e => e.send_day)) + 7
      : 0;

    setEditingEmail({
      id: null,
      product_id: selectedProduct,
      send_day: nextDay,
      subject: '',
      body: '',
      upsell_product_id: null,
      sidesell_product_id: null
    });
    setShowEmailModal(true);
  };

  const handleSaveEmail = async () => {
    if (!editingEmail.subject.trim() || !editingEmail.body.trim()) {
      setError('Please enter subject and body');
      return;
    }

    setSaving(true);
    try {
      const url = editingEmail.id
        ? `/api/email-sequences/${editingEmail.id}`
        : '/api/email-sequences';

      const response = await fetch(url, {
        method: editingEmail.id ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingEmail)
      });

      if (response.ok) {
        setSuccess('Email saved successfully');
        setShowEmailModal(false);
        fetchData(true);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save email');
      }
    } catch (err) {
      setError('Failed to save email');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm('Delete this email from the sequence?')) return;

    try {
      const response = await fetch(`/api/email-sequences/${emailId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Email deleted');
        fetchData(true);
      }
    } catch (err) {
      setError('Failed to delete email');
    }
  };

  const handleGenerateSequence = async () => {
    if (!selectedProduct) {
      setError('Please select a product first');
      return;
    }

    setGenerating(true);
    setError('');
    try {
      const response = await fetch('/api/email-sequences/generate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: selectedProduct })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Generated ${data.emails_created} emails for the sequence`);
        fetchData(true);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to generate sequence');
      }
    } catch (err) {
      setError('Failed to generate sequence');
    } finally {
      setGenerating(false);
    }
  };

  const selectedProductData = products.find(p => p.id === selectedProduct);
  const productSequence = selectedProduct ? getSequenceForProduct(selectedProduct) : [];

  // Get upsell products for this product
  const getUpsellProducts = () => {
    if (!selectedProduct) return [];
    const paths = upsellOptions.filter(p => p.from_product_id === selectedProduct);
    return paths.map(p => products.find(prod => prod.id === p.to_product_id)).filter(Boolean);
  };

  // Get sidesell products (same tier, different products)
  const getSidesellProducts = () => {
    if (!selectedProduct || !selectedProductData) return [];
    return products.filter(p =>
      p.id !== selectedProduct &&
      p.position === selectedProductData.position
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading email sequences...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h1 style={{ color: colors.greyDarkest }}>
            <span className="me-2">📧</span>
            Post-Purchase Email Sequences
          </h1>
          <p className="text-muted">
            Design email sequences that nurture customers after purchase. AI uses your Customer Experience design to write relevant content.
          </p>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Row>
        {/* Left: Product Selector & Sequence */}
        <Col lg={8}>
          {/* Product Selector */}
          <Card className="mb-4 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">Email Sequence for Product</h5>
                </Col>
                <Col xs="auto">
                  <Form.Select
                    value={selectedProduct || ''}
                    onChange={(e) => setSelectedProduct(e.target.value ? parseInt(e.target.value) : null)}
                    style={{ maxWidth: '300px' }}
                  >
                    <option value="">Select a product...</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              {/* AI Generate Button */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <strong>{productSequence.length}</strong> emails in sequence
                </div>
                <Button
                  variant="primary"
                  onClick={handleGenerateSequence}
                  disabled={generating}
                  style={{ backgroundColor: colors.goldMid, borderColor: colors.goldMid }}
                >
                  {generating ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Generating...
                    </>
                  ) : (
                    <>✨ AI Assist - Write Sequence</>
                  )}
                </Button>
              </div>

              {/* Email Sequence Table */}
              {productSequence.length > 0 ? (
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: '120px' }}>Timing</th>
                      <th>Subject</th>
                      <th style={{ width: '150px' }}>Upsell/Sidesell</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productSequence.map((email) => (
                      <tr key={email.id}>
                        <td>
                          <Badge bg="secondary">
                            Purchase + {email.send_day} {email.send_day === 1 ? 'day' : 'days'}
                          </Badge>
                        </td>
                        <td>
                          <strong>{email.subject}</strong>
                          <br />
                          <small className="text-muted">
                            {email.body?.substring(0, 80)}...
                          </small>
                        </td>
                        <td>
                          {email.upsell_product_id && (
                            <Badge bg="success" className="me-1">Upsell</Badge>
                          )}
                          {email.sidesell_product_id && (
                            <Badge bg="info">Sidesell</Badge>
                          )}
                          {!email.upsell_product_id && !email.sidesell_product_id && (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            onClick={() => handleEditEmail(email)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteEmail(email.id)}
                          >
                            ×
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-5">
                  <p className="text-muted mb-3">No emails in this sequence yet</p>
                  <Button variant="outline-primary" onClick={handleAddEmail}>
                    + Add First Email
                  </Button>
                  <span className="mx-2 text-muted">or</span>
                  <Button
                    variant="primary"
                    onClick={handleGenerateSequence}
                    style={{ backgroundColor: colors.goldMid, borderColor: colors.goldMid }}
                  >
                    ✨ Generate with AI
                  </Button>
                </div>
              )}

              {productSequence.length > 0 && (
                <div className="text-center mt-3">
                  <Button variant="outline-primary" onClick={handleAddEmail}>
                    + Add Email to Sequence
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right: Context Panel */}
        <Col lg={4}>
          {/* Customer Experience Context */}
          <Card className="mb-3 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyLight }}>
              <h6 className="mb-0">📋 Customer Experience Context</h6>
            </Card.Header>
            <Card.Body className="small">
              {customerExperience ? (
                <>
                  <div className="mb-2">
                    <strong>Purchase:</strong>
                    <br />
                    <span className="text-muted">{customerExperience.purchase || 'Not defined'}</span>
                  </div>
                  <div className="mb-2">
                    <strong>Onboarding:</strong>
                    <br />
                    <span className="text-muted">{customerExperience.onboarding || 'Not defined'}</span>
                  </div>
                  <div className="mb-2">
                    <strong>Success Metrics:</strong>
                    <br />
                    <span className="text-muted">{customerExperience.success_metrics || 'Not defined'}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mt-2"
                    onClick={() => navigate('/customers')}
                  >
                    Edit Experience →
                  </Button>
                </>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted mb-2">No customer experience defined yet</p>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => navigate('/customers')}
                  >
                    Define Experience
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Upsell Options */}
          <Card className="mb-3 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyLight }}>
              <h6 className="mb-0">⬆️ Upsell Options</h6>
            </Card.Header>
            <Card.Body className="small">
              {getUpsellProducts().length > 0 ? (
                <ul className="mb-0 ps-3">
                  {getUpsellProducts().map(p => (
                    <li key={p.id}>
                      {p.name} - ${(parseFloat(p.price) || 0).toLocaleString()}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">No upsell paths defined</p>
              )}
            </Card.Body>
          </Card>

          {/* Sidesell Options */}
          <Card className="shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyLight }}>
              <h6 className="mb-0">↔️ Sidesell Options</h6>
            </Card.Header>
            <Card.Body className="small">
              {getSidesellProducts().length > 0 ? (
                <ul className="mb-0 ps-3">
                  {getSidesellProducts().map(p => (
                    <li key={p.id}>
                      {p.name} - ${(parseFloat(p.price) || 0).toLocaleString()}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">No sidesell products at this tier</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Email Editor Modal */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>
            {editingEmail?.id ? 'Edit Email' : 'Add Email to Sequence'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Send Timing</Form.Label>
                <div className="d-flex align-items-center">
                  <span className="me-2">Purchase +</span>
                  <Form.Control
                    type="number"
                    min="0"
                    value={editingEmail?.send_day || 0}
                    onChange={(e) => setEditingEmail({...editingEmail, send_day: parseInt(e.target.value) || 0})}
                    style={{ width: '80px' }}
                  />
                  <span className="ms-2">days</span>
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Subject Line</Form.Label>
                <Form.Control
                  type="text"
                  value={editingEmail?.subject || ''}
                  onChange={(e) => setEditingEmail({...editingEmail, subject: e.target.value})}
                  placeholder="Enter email subject..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email Body</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={editingEmail?.body || ''}
                  onChange={(e) => setEditingEmail({...editingEmail, body: e.target.value})}
                  placeholder="Enter email content..."
                />
              </Form.Group>

              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Conditional Upsell</Form.Label>
                    <Form.Select
                      value={editingEmail?.upsell_product_id || ''}
                      onChange={(e) => setEditingEmail({...editingEmail, upsell_product_id: e.target.value ? parseInt(e.target.value) : null})}
                    >
                      <option value="">No upsell</option>
                      {getUpsellProducts().map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Upsell block only shows if customer hasn't purchased this
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Conditional Sidesell</Form.Label>
                    <Form.Select
                      value={editingEmail?.sidesell_product_id || ''}
                      onChange={(e) => setEditingEmail({...editingEmail, sidesell_product_id: e.target.value ? parseInt(e.target.value) : null})}
                    >
                      <option value="">No sidesell</option>
                      {getSidesellProducts().map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Sidesell block only shows if customer hasn't purchased this
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Col>

            {/* Context Panel in Modal */}
            <Col md={4} style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
              <h6 className="mb-3">📋 Context</h6>

              {customerExperience && (
                <div className="small mb-3">
                  <strong>Customer Experience:</strong>
                  <ul className="ps-3 mb-0">
                    {customerExperience.onboarding && <li>Onboarding: {customerExperience.onboarding.substring(0, 50)}...</li>}
                    {customerExperience.success_metrics && <li>Success: {customerExperience.success_metrics.substring(0, 50)}...</li>}
                  </ul>
                </div>
              )}

              {editingEmail?.upsell_product_id && (
                <div className="small mb-2">
                  <Badge bg="success">Upsell Block</Badge>
                  <p className="mb-0 mt-1 text-muted">
                    Content for {products.find(p => p.id === editingEmail.upsell_product_id)?.name} will only show to non-purchasers
                  </p>
                </div>
              )}

              {editingEmail?.sidesell_product_id && (
                <div className="small">
                  <Badge bg="info">Sidesell Block</Badge>
                  <p className="mb-0 mt-1 text-muted">
                    Content for {products.find(p => p.id === editingEmail.sidesell_product_id)?.name} will only show to non-purchasers
                  </p>
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEmail}
            disabled={saving}
            style={{ backgroundColor: colors.goldMid, borderColor: colors.goldMid }}
          >
            {saving ? 'Saving...' : 'Save Email'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default EmailSequences;
